import { formatDateObject, formatDuration } from 'format-helpers'
import { EventLog, MountpointStatus, ServerStat } from '../api'

export interface StabilityCounts {
  optimal: number
  acceptable: number
  weak: number
  veryWeak: number
  withoutConnection: number
}

export const optimalPercent = 99.9
export const acceptablePercent = 99.5
export const weakPercent = 99
export const withoutConnectionPercent = 0

export function getUptimeStabilityCounts(
  serverStat: ServerStat,
  from: Date,
  to: Date,
): StabilityCounts {
  const res: StabilityCounts = {
    optimal: 0,
    acceptable: 0,
    weak: 0,
    veryWeak: 0,
    withoutConnection: 0,
  }

  for (let v in serverStat.mountpoints) {
    const { percent } = getUptimeFromEventLogs(
      JSON.parse(serverStat.mountpoints[v].eventLog),
      from,
      to,
    )
    if (percent > optimalPercent) {
      res.optimal++
    } else if (percent > acceptablePercent) {
      res.acceptable++
    } else if (percent > weakPercent) {
      res.weak++
    } else if (percent === withoutConnectionPercent) {
      res.withoutConnection++
    } else {
      res.veryWeak++
    }
  }

  return res
}

export interface Uptime {
  percent: number
  unavailablePercent: number
  status: 'optimal' | 'acceptable' | 'weak' | 'veryWeak' | 'withoutConnection'
}

export function getUptimeFromEventLogs(
  eventLogs: EventLog[],
  startDate: Date,
  endDate: Date,
): Uptime {
  const startDateInSeconds = startDate.getTime() / 1000
  const endDateInSeconds = endDate.getTime() / 1000

  if (eventLogs.length === 0)
    return { percent: 0, unavailablePercent: 0, status: 'withoutConnection' }

  // const filteredLogs = eventLogs.filter(
  //   (log) => log[0] > startDateInSeconds && log[0] < endDateInSeconds,
  // )

  // if (filteredLogs.length === 0) {
  //   const lastLog = eventLogs[eventLogs.length - 1]

  //   if (lastLog[1] === MountpointStatus.Online)
  //     filteredLogs.push([startDateInSeconds, MountpointStatus.Online])
  //   else
  //     return { percent: 0, unavailablePercent: 0, status: 'withoutConnection' }
  // }

  let disconnectedTotalSeconds = 0

  for (let i = 0; i < eventLogs.length; i++) {
    const currentLog = eventLogs[i]
    const nextLog = eventLogs[i + 1]

    if (
      currentLog[1] === MountpointStatus.Offline &&
      nextLog[1] === MountpointStatus.Online
    )
      disconnectedTotalSeconds += nextLog[0] - currentLog[0]
  }

  const firstLog = eventLogs[0]

  /**Unavailable logs total seconds */
  const unavailableTotalSeconds = Math.max(firstLog[0] - startDateInSeconds, 0)

  /**Available logs total seconds */
  const availableTotalSeconds =
    endDateInSeconds - startDateInSeconds - unavailableTotalSeconds

  // Check last filtered log
  const lastFilteredLog = eventLogs[eventLogs.length - 1]
  if (lastFilteredLog[1] === MountpointStatus.Offline)
    disconnectedTotalSeconds += endDateInSeconds - lastFilteredLog[0]

  const percent = (1 - disconnectedTotalSeconds / availableTotalSeconds) * 100
  const unavailablePercent =
    (unavailableTotalSeconds / (endDateInSeconds - startDateInSeconds)) * 100
  let status: Uptime['status'] = 'withoutConnection'

  if (percent >= optimalPercent) status = 'optimal'
  else if (percent >= acceptablePercent) status = 'acceptable'
  else if (percent >= weakPercent) status = 'weak'
  else status = 'veryWeak'

  const filterDuration = endDateInSeconds - startDateInSeconds
  console.log('unavailable', (unavailableTotalSeconds * 100) / filterDuration)
  console.log('available', (availableTotalSeconds * 100) / filterDuration)
  console.log(
    'available online',
    ((availableTotalSeconds - disconnectedTotalSeconds) * 100) / filterDuration,
  )
  console.log(
    'available offline',
    (disconnectedTotalSeconds * 100) / filterDuration,
  )

  return { percent, unavailablePercent, status }
}

export function getUptimeColor(percent: number): string {
  if (percent === 0) return '#5F6368'
  else if (percent >= optimalPercent) {
    return '#198d10'
  } else if (percent >= acceptablePercent) {
    return '#a1bc12'
  } else if (percent >= weakPercent) {
    return '#ee3b3b'
  } else {
    return '#b00c0c'
  }
}

export interface UptimeInfo {
  onlineTotalSeconds: number
  offlineTotalSeconds: number
  disconnections: { start: string; end: string; duration: string }[]
  onlinePercent: number
  allConnections: {
    start: string
    end: string
    duration: string
    /**In percent */
    startPosition: number
    /**In percent */
    percent: number
    status: MountpointStatus
  }[]
  status: 'optimal' | 'acceptable' | 'weak' | 'veryWeak' | 'withoutConnection'
}
export function calulateMountpointUptimeInfo(
  logs: EventLog[],
  /**In seconds */
  startDate: number,
  /**In seconds */
  endDate: number,
  name: string,
): UptimeInfo {
  if (logs.length === 0) {
    return {
      onlineTotalSeconds: 0,
      offlineTotalSeconds: 0,
      disconnections: [],
      onlinePercent: 0,
      allConnections: [],
      status: 'withoutConnection',
    }
  }

  const filteredLogs = logs.reduce<[number, number, number][]>(
    (acc, log, index) => {
      if (log[0] >= startDate && log[1] <= endDate) acc.push([...log, index])
      return acc
    },
    [],
  )

  let onlineTotalSeconds = 0
  let offlineTotalSeconds = 0
  let disconnections: UptimeInfo['disconnections'] = []
  let allConnections: UptimeInfo['allConnections'] = []

  let startPosition = 0
  const filterDuration = endDate - startDate

  // Check first filtered log
  if (filteredLogs.length === 0 && logs.length !== 0) {
    allConnections.push({
      start: formatDateObject(startDate),
      end: formatDateObject(endDate),
      duration: formatDuration(filterDuration),
      percent: 100,
      startPosition: 0,
      status: MountpointStatus.Online,
    })
    onlineTotalSeconds += filterDuration
  } else if (
    filteredLogs[0]?.[2] &&
    logs[filteredLogs[0][2] - 1][1] > startDate
  ) {
    const lastLog = logs[filteredLogs[0][2] - 1]
    const lastLogDuration = lastLog[1] - startDate
    const lastLogPercent = (lastLogDuration * 100) / filterDuration
    const lastLogTemp = {
      start: formatDateObject(startDate),
      end: formatDateObject(lastLog[1]),
      duration: formatDuration(lastLogDuration),
    }
    disconnections.push(lastLogTemp)
    allConnections.push({
      ...lastLogTemp,
      startPosition,
      percent: lastLogPercent,
      status: MountpointStatus.Offline,
    })
    offlineTotalSeconds += lastLogDuration
    startPosition += lastLogPercent
  } else {
    const firstFilteredLog = filteredLogs[0]
    const firstFilteredLogDuration = firstFilteredLog[0] - startDate
    const firstFilteredLogPercent =
      (firstFilteredLogDuration * 100) / filterDuration
    const firstFilteredLogTemp = {
      start: formatDateObject(startDate),
      end: formatDateObject(firstFilteredLog[0]),
      duration: formatDuration(firstFilteredLogDuration),
    }
    allConnections.push({
      ...firstFilteredLogTemp,
      startPosition,
      percent: firstFilteredLogPercent,
      status: MountpointStatus.Online,
    })
    onlineTotalSeconds += firstFilteredLogDuration
    startPosition += firstFilteredLogPercent
  }

  for (let i = 0; i < filteredLogs.length; i++) {
    const currentLog = filteredLogs[i]
    const nextLog = filteredLogs[i + 1]

    const offlineDuration = currentLog[1] - currentLog[0]
    const offlinePercent = (offlineDuration * 100) / filterDuration
    const offlineTemp = {
      start: formatDateObject(currentLog[0]),
      end: formatDateObject(currentLog[1]),
      duration: formatDuration(offlineDuration),
    }
    disconnections.push(offlineTemp)
    allConnections.push({
      ...offlineTemp,
      startPosition,
      percent: offlinePercent,
      status: MountpointStatus.Offline,
    })
    offlineTotalSeconds += offlineDuration
    startPosition += offlinePercent

    if (!nextLog) break

    const onlineDuration = nextLog[0] - currentLog[1]
    const onlinePercent = (onlineDuration * 100) / filterDuration
    const onlineTemp = {
      start: formatDateObject(currentLog[1]),
      end: formatDateObject(nextLog[0]),
      duration: formatDuration(onlineDuration),
    }
    allConnections.push({
      ...onlineTemp,
      startPosition,
      percent: onlinePercent,
      status: MountpointStatus.Online,
    })
    onlineTotalSeconds += onlineDuration
    startPosition += onlinePercent
  }

  // Check last filtered log
  const lastFilteredLog = filteredLogs[filteredLogs.length - 1]
  if (lastFilteredLog) {
    const lastFilteredLogDuration = endDate - lastFilteredLog[1]

    /**
     * When mountpoint is offline
     * the end time will get updated every 10s
     * so to make sure the mountpoint is currently online
     * the duration has to be more than 10s.
     */
    if (lastFilteredLogDuration > 10) {
      const lastFilteredLogPercent =
        (lastFilteredLogDuration * 100) / filterDuration
      const lastFilteredLogTemp = {
        start: formatDateObject(lastFilteredLog[1]),
        end: formatDateObject(endDate),
        duration: formatDuration(lastFilteredLogDuration),
      }
      onlineTotalSeconds += lastFilteredLogDuration
      allConnections.push({
        ...lastFilteredLogTemp,
        startPosition,
        percent: lastFilteredLogPercent,
        status: MountpointStatus.Online,
      })
    }
  }

  const onlinePercent = (onlineTotalSeconds * 100) / filterDuration

  let status = 'veryWeak' as Uptime['status']

  if (onlinePercent >= optimalPercent) status = 'optimal'
  else if (onlinePercent >= acceptablePercent) status = 'acceptable'
  else if (onlinePercent >= weakPercent) status = 'weak'

  // console.table(
  //   filteredLogs.map((log) => [
  //     formatDateObject(log[0]),
  //     formatDateObject(log[1]),
  //     log[2],
  //   ]),
  // )
  // console.table(allConnections)

  return {
    onlineTotalSeconds,
    offlineTotalSeconds,
    disconnections,
    onlinePercent,
    allConnections,
    status,
  }
}
